import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import OptimizedImage from './OptimizedImage';
import { IMAGE_BLURS } from '../data/imageBlurs';

const steps = [
    {
        id: 'step-1',
        title: 'Check in',
        description: "A 30-second check-in maps your current state, focus, and energy levels to identify your cognitive position.",
        image: '/images/check_in_mockup.webp',
        imageMobile: '/images/check_in_mockup-mobile.webp',
        imageBlur: IMAGE_BLURS.check_in_mockup
    },
    {
        id: 'step-2',
        title: 'Practice',
        description: "Receive a personalized audio practice. Every session is unique and calibrated for exactly where you are.",
        image: '/images/session_mockup.webp',
        imageMobile: '/images/session_mockup-mobile.webp',
        imageBlur: IMAGE_BLURS.session_mockup
    },
    {
        id: 'step-3',
        title: 'Get insights',
        description: "Get science backed insights into your state.",
        image: '/images/insights_mockup.webp',
        imageMobile: '/images/insights_mockup-mobile.webp',
        imageBlur: IMAGE_BLURS.insights_mockup
    }
];

const ProcessSection: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    // Logic Refs
    const activeIndexRef = useRef(0);
    const isCoolingDown = useRef(false);
    const isEngaged = useRef(false);


    useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
            // Complete reset when section is totally gone
            if (!entry.isIntersecting) {
                isEngaged.current = false;
                isCoolingDown.current = false;
            }
        }, { threshold: 0.1 });
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleWheel = (e: WheelEvent) => {
            const container = containerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const absDelta = Math.abs(e.deltaY);
            const direction = e.deltaY > 0 ? 1 : -1;

            // Boundaries
            const isAtTop = rect.top >= -20 && rect.top <= 80;
            const isAtBottom = rect.bottom >= window.innerHeight - 80 && rect.bottom <= window.innerHeight + 20;
            const inside = rect.top <= 5 && rect.bottom >= window.innerHeight - 5;

            // 1. ENTRY LOGIC (From Top or Bottom)
            if (!isEngaged.current && !isCoolingDown.current) {
                const enteringFromTop = isAtTop && direction > 0;
                const enteringFromBottom = isAtBottom && direction < 0;

                if (enteringFromTop || enteringFromBottom) {
                    e.preventDefault();
                    isEngaged.current = true;
                    isCoolingDown.current = true;

                    const targetIdx = enteringFromTop ? 0 : steps.length - 1;
                    setActiveIndex(targetIdx);

                    setTimeout(() => { isCoolingDown.current = false; }, 400);
                    return;
                }
            }

            // 2. NAVIGATION LOGIC
            if (isEngaged.current && inside) {
                if (isCoolingDown.current) {
                    e.preventDefault();
                    return;
                }

                // Threshold check: Must be a deliberate gesture
                if (absDelta > 20) {
                    const next = activeIndexRef.current + direction;

                    if (next >= 0 && next < steps.length) {
                        e.preventDefault();
                        isCoolingDown.current = true;
                        setActiveIndex(next);

                        window.scrollTo({
                            top: container.offsetTop + (next * window.innerHeight),
                            behavior: 'smooth'
                        });

                        setTimeout(() => { isCoolingDown.current = false; }, 600);
                    } else {
                        // EXIT GESTURE (Natural Release)
                        isEngaged.current = false;
                        isCoolingDown.current = true;
                        // No jump/scrollTo here - just let native scroll take over

                        setTimeout(() => { isCoolingDown.current = false; }, 800);
                    }
                } else {
                    e.preventDefault(); // Block micro-drifting
                }
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        return () => window.removeEventListener('wheel', handleWheel);
    }, []);

    // Touch gesture handling removed - using explicit button controls on mobile instead

    const activeStep = steps[activeIndex];

    return (
        <>
            {/* Mobile Layout - Simple vertical stack, no switching */}
            <div className="md:hidden bg-canvas">
                {steps.map((step) => (
                    <section key={step.id} className="py-16 px-6">
                        {/* Text */}
                        <div className="mb-8">
                            <h2 className="text-5xl font-normal tracking-[-0.02em] text-text-primary mb-6 leading-[1.1]">
                                {step.title}
                            </h2>
                            <p className="text-lg text-text-primary leading-relaxed text-balance max-w-2xl">
                                {step.description}
                            </p>
                        </div>

                        {/* Image - Full width */}
                        <div className="relative w-[100vw] -mx-6 aspect-square">
                            <div
                                className="absolute inset-0 overflow-hidden"
                                style={{ transform: 'translateZ(0)', WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
                            >
                                <OptimizedImage
                                    src={step.image}
                                    mobileSrc={step.imageMobile}
                                    blurSrc={step.imageBlur}
                                    alt={step.title}
                                    containerClassName="w-full h-full"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </section>
                ))}
            </div>

            {/* Desktop Layout - Sticky scroll behavior */}
            <div ref={containerRef} className="hidden md:block relative bg-canvas" style={{ height: `${steps.length * 100}vh` }}>
                <div className="sticky top-0 h-screen w-full flex items-center overflow-hidden">
                    <div className="w-full px-24 lg:px-32 flex flex-row items-center gap-16">
                        <div className="flex-1 h-[400px] flex items-center relative">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeStep.id}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -15 }}
                                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    className="absolute inset-0 flex flex-col justify-center"
                                >
                                    <h2 className="text-7xl lg:text-8xl font-normal tracking-[-0.02em] text-text-primary mb-8 leading-[1.1]">
                                        {activeStep.title}
                                    </h2>
                                    <p className="text-xl text-text-primary leading-relaxed text-balance max-w-2xl">
                                        {activeStep.description}
                                    </p>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        <div className="relative w-full max-w-[600px] aspect-square flex-shrink-0">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`${activeStep.id}-img`}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className="absolute inset-0 rounded-2xl overflow-hidden shadow-2xl"
                                    style={{ transform: 'translateZ(0)', WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
                                >
                                    <OptimizedImage
                                        src={activeStep.image}
                                        mobileSrc={activeStep.imageMobile}
                                        blurSrc={activeStep.imageBlur}
                                        alt={activeStep.title}
                                        containerClassName="w-full h-full"
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Indicators */}
                    {isVisible && (
                        <div className="absolute right-8 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-50">
                            {steps.map((_, i) => (
                                <div key={i} className={`w-2 h-2 rounded-full transition-all duration-300 ${activeIndex === i ? 'bg-text-primary scale-125' : 'bg-text-primary/30'}`} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ProcessSection;
